import React, { useEffect, useState, useContext } from 'react';
import { useSnackbar } from 'notistack';
import {
  Button,
  FormControl,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  MenuItem,
  Select,
  OutlinedInput,
  Typography,
} from '@mui/material';
import { getAllClientsApi } from "../api's/ClientApi";
import { createEngagementApi } from "../api's/EngagementApi";
import { ENGAGEMENT_PAGE } from '../utils/commonConstants';
import { AuthContext } from '../context/AuthProvider';
import { getDataModelsList } from "../api's/DataModelApi";
import LoadingIcon from './LoadingIcon';

export default function AddEngagementModal({ handleCloseModal, openModal, fetchData }) {
  const { userId } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [clientData, setClientData] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [engagementName, setEngagementName] = useState('');
  const [clientName, setClientName] = useState('');
  const [engagementNameError, setEngagementNameError] = useState('');
  const [dataModel, setDataModel] = useState({});
  const [dataModelList, setDataModelList] = useState([]);

  // const nameRegex = /^\S{1,35}$/;
  const nameRegex = /^[a-zA-Z0-9 ]{1,35}$/;

  const handleChange = (event, source) => {
    switch (source) {
      case 'clientName': {
        const newValue = event.target.value;

        setClientName(newValue);

        break;
      }
      case 'engagementName': {
        const newValue = event.target.value;

        setEngagementName(newValue);
        if (newValue.trim() === '') {
          setEngagementNameError('Engagement Name is required.');
        } else if (!newValue.match(nameRegex)) {
          setEngagementNameError(`NO SPECIAL CHARACTER ALLOWED.`);
        } else {
          setEngagementNameError('');
        }

        break;
      }

      case 'dataModel':
        setDataModel(event.target.value);
        break;

      default:
        break;
    }
  };

  const handleOnClickAdd = () => {
    if (!dataModel || Object.keys(dataModel).length === 0) {
      enqueueSnackbar('Data model is required!', { variant: 'error' });
      return;
    }
    if (engagementName.match(nameRegex)) {
      if (clientName) {
        if (dataModel) {
          createEngagement();
        } else {
          enqueueSnackbar('Data Model not selected.', { variant: 'error' });
        }
      } else {
        enqueueSnackbar('Client Name not selected.', { variant: 'error' });
      }
    } else {
      enqueueSnackbar(`INVALID ENGAGEMENT NAME. NO SPECIAL CHARACTER ALLOWED.`, { variant: 'error' });
    }
  };

  const handleOnCancel = () => {
    handleCloseModal();
    setClientName('');
    setEngagementName('');
    setEngagementNameError('');
    setDataModel({});
  };

  // getDataModelsList();

  const fetchDataModelsList = async () => {
    setLoading(true);
    try {
      const response = await getDataModelsList();
      if (response.status === 200) {
        setDataModelList(response.data);
      }
    } catch (error) {
      // setDataModelList(dummyDataModel);
      console.error(error);
      enqueueSnackbar('Error while fetching Data Models!', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    } finally {
      setLoading(false);
    }
  };

  const createEngagement = async () => {
    setLoading(true);
    const selectedClientId = clientData.find((item) => item.client_name === clientName).client_id;
    const data = {
      engagement_name: engagementName,
      engagement_type: 'daily',
      user_id: userId,
      client_id: selectedClientId,
      data_model_id: dataModel.id,
      data_model_name: dataModel.modelTypeName,
    };
    try {
      const response = await createEngagementApi(data);
      if (response.status === 201) {
        enqueueSnackbar('Engagement added successfully!', {
          variant: 'success',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
        fetchData();
        handleOnCancel();
      }
    } catch (error) {
      console.log(error);
      if (error.response?.status === 403) {
        enqueueSnackbar('You have only Read Permission !!', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      } else if (error.response?.status === 409) {
        enqueueSnackbar('Engagement with the same name already exists!', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      } else if (error.response.status === 500) {
        enqueueSnackbar('Internal Server Error!!', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAllClients = async () => {
    setLoading(true);
    try {
      const response = await getAllClientsApi();
      if (response.status === 200) {
        setClientData(response.data);
      }
    } catch (error) {
      console.log(error);
      if (error.response.status === 404) {
        enqueueSnackbar('No data found!', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      }
      if (error.response.status === 500) {
        enqueueSnackbar(error.response.data || 'Internal Server Error!', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (openModal) {
      fetchAllClients();
      fetchDataModelsList();
    }
  }, [openModal]);

  useEffect(() => {
    setEngagementNameError('');
  }, []);

  return (
    <Dialog
      open={openModal}
      onClose={handleOnCancel}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle sx={{ color: '#43425D' }}> {ENGAGEMENT_PAGE.addEngagementModal}</DialogTitle>
      <DialogContent>
        <Box component="form" display="flex" justifyContent="space-between" gap="10px" alignItems="end" width="450px">
          <FormControl variant="standard" sx={{ minWidth: 220 }}>
            <Typography
              variant="p"
              component="p"
              display="flex"
              alignContent="flex-start"
              sx={{ color: '#8798AD', fontSize: '11px' }}
            >
              Engagement Name*
            </Typography>
            {engagementNameError && (
              <Typography
                variant="caption"
                sx={{ color: 'error.main', fontSize: '11px', mt: 0.5 }}
              >
                {engagementNameError}
              </Typography>
            )}
            <TextField
              size="small"
              required
              id="engagementName"
              name="engagementName"
              autoComplete="engagementName"
              variant="outlined"
              value={engagementName}
              error={!!engagementNameError}
              onChange={(e) => handleChange(e, 'engagementName')}
              sx={{ background: '#2E5BFF14', mt: 0.5 }}
            />
          </FormControl>
          <FormControl sx={{ minWidth: 220 }}>
            <Typography
              variant="p"
              component="p"
              display="flex"
              alignContent="flex-start"
              sx={{ color: '#8798AD', fontSize: '11px' }}
            >
              Client*
            </Typography>
            <Select
              size="small"
              labelId="clientName"
              id="clientName"
              value={clientName}
              onChange={(e) => handleChange(e, 'clientName')}
              input={<OutlinedInput />}
              sx={{ backgroundColor: '#2E5BFF14', height: '40px' }}
            >
              {clientData.map((item) => (
                <MenuItem key={item.client_id} value={item.client_name}>
                  {item.client_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <FormControl sx={{ minWidth: 220, mt: 2 }}>
          <Typography sx={{ color: '#8798AD', fontSize: '11px' }}>Data Model*</Typography>
          <Select
            size="small"
            id="dataModel"
            value={dataModel.modelTypeName}
            onChange={(e) => handleChange(e, 'dataModel')}
            input={<OutlinedInput />}
            sx={{ backgroundColor: '#2E5BFF14', height: '40px' }}
          >
            {dataModelList?.map((model) => (
              <MenuItem key={model.id} value={model}>
                {model.modelTypeName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {loading && <LoadingIcon />}
      </DialogContent>
      <DialogActions sx={{ pr: 3, pb: 2 }}>
        <Button
          onClick={handleOnCancel}
          variant="outlined"
          disabled={loading}
          sx={{ textTransform: 'capitalize', color: '#43425D', border: '1px solid #43425D' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleOnClickAdd}
          variant="contained"
          disabled={loading}
          sx={{ backgroundColor: '#00043C', textTransform: 'capitalize', '&:hover': { backgroundColor: '#00043C' } }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
